const AP_UNAVAILABLE_TITLE = 'Inte tillgängligt med valda filter';

function apBool(value) {
  return value === true || value === 'true';
}

function apIsActive(button) {
  return button?.classList.contains('is-active') || button?.getAttribute('aria-pressed') === 'true';
}

function apSetButtonAvailability(button, available, keepEnabled = false) {
  if (!(button instanceof HTMLButtonElement)) return;

  const unavailable = !available && !keepEnabled;
  button.disabled = unavailable;
  button.classList.toggle('is-unavailable', unavailable);
  button.setAttribute('aria-disabled', unavailable ? 'true' : 'false');

  if (unavailable) {
    button.title = AP_UNAVAILABLE_TITLE;
  } else if (button.title === AP_UNAVAILABLE_TITLE) {
    button.removeAttribute('title');
  }
}

function apSetFeatureAvailability(input, available, wrapperSelector) {
  if (!(input instanceof HTMLInputElement)) return;

  const keepEnabled = input.checked;
  const unavailable = !available && !keepEnabled;
  const wrapper = input.closest(wrapperSelector);

  input.disabled = unavailable;
  input.setAttribute('aria-disabled', unavailable ? 'true' : 'false');

  if (wrapper instanceof HTMLElement) {
    wrapper.classList.toggle('is-unavailable', unavailable);
    wrapper.setAttribute('aria-disabled', unavailable ? 'true' : 'false');

    if (unavailable) {
      wrapper.title = AP_UNAVAILABLE_TITLE;
    } else if (wrapper.title === AP_UNAVAILABLE_TITLE) {
      wrapper.removeAttribute('title');
    }
  }
}

function apScheduleUpdate(callback) {
  requestAnimationFrame(() => requestAnimationFrame(callback));
}

function initArbetsPROAllProductsAvailability() {
  const root = document.querySelector('[data-ap-all-filter-section]');
  if (!root || root.dataset.apAvailabilityInitialized === 'true') return;

  const sectionId = root.getAttribute('data-ap-all-filter-section');
  const dataNode = document.getElementById(`ap-all-filter-data-${sectionId}`);
  if (!dataNode) return;

  let products = [];
  try {
    products = JSON.parse(dataNode.textContent || '[]');
  } catch (error) {
    return;
  }

  root.dataset.apAvailabilityInitialized = 'true';

  const preparedProducts = products.map((product) => ({
    ...product,
    category: String(product.category || ''),
    sizes: Array.isArray(product.sizes) ? product.sizes.map(String) : [],
    features: Object.fromEntries(
      Object.entries(product.features || {}).map(([key, value]) => [key, apBool(value)])
    )
  }));

  const categoryButtons = Array.from(root.querySelectorAll('[data-ap-category]'));

  const getState = () => {
    const activeCategory = categoryButtons.find(apIsActive);
    const category = activeCategory?.dataset.apCategory || '';
    const activeSize = Array.from(root.querySelectorAll('[data-ap-size]')).find(apIsActive);
    const featureInputs = Array.from(
      root.querySelectorAll(`[data-ap-feature][data-ap-feature-category="${category}"]`)
    );

    return {
      category,
      size: activeSize?.dataset.apSize || '',
      features: new Set(
        featureInputs
          .filter((input) => input instanceof HTMLInputElement && input.checked)
          .map((input) => input.value)
      )
    };
  };

  const matches = (product, category, size, features) => {
    if (!category || product.category !== category) return false;
    if (size && !product.sizes.includes(String(size))) return false;
    return Array.from(features).every((feature) => product.features?.[feature] === true);
  };

  const updateAvailability = () => {
    const state = getState();

    categoryButtons.forEach((button) => {
      const category = button.dataset.apCategory || '';
      const active = category === state.category;
      const available = category === '' || preparedProducts.some((product) => product.category === category);
      apSetButtonAvailability(button, available, active);
    });

    Array.from(root.querySelectorAll('[data-ap-size]')).forEach((button) => {
      const size = button.dataset.apSize || '';
      const active = apIsActive(button);
      const available =
        size === '' ||
        !['skor', 'handskar'].includes(state.category) ||
        preparedProducts.some((product) => matches(product, state.category, size, state.features));
      apSetButtonAvailability(button, available, active);
    });

    Array.from(root.querySelectorAll('[data-ap-feature]')).forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      const inputCategory = input.dataset.apFeatureCategory || '';
      if (inputCategory !== state.category) {
        apSetFeatureAvailability(input, true, '.ap-feature-option');
        return;
      }

      const candidateFeatures = new Set(state.features);
      candidateFeatures.add(input.value);

      const available = preparedProducts.some((product) =>
        matches(product, state.category, state.size, candidateFeatures)
      );

      apSetFeatureAvailability(input, available, '.ap-feature-option');
    });
  };

  root.addEventListener('click', () => apScheduleUpdate(updateAvailability));
  root.addEventListener('change', () => apScheduleUpdate(updateAvailability));

  const sizeContainer = root.querySelector('[data-ap-sizes]');
  if (sizeContainer) {
    new MutationObserver(() => apScheduleUpdate(updateAvailability)).observe(sizeContainer, {
      childList: true,
      subtree: true
    });
  }

  apScheduleUpdate(updateAvailability);
}

function initArbetsPROShoeAvailability() {
  const root = document.querySelector('[data-ap-shoe-filter-section]');
  if (!root || root.dataset.apAvailabilityInitialized === 'true') return;

  const sectionId = root.getAttribute('data-ap-shoe-filter-section');
  const dataNode = document.getElementById(`ap-shoe-filter-data-${sectionId}`);
  if (!dataNode) return;

  let products = [];
  try {
    products = JSON.parse(dataNode.textContent || '[]');
  } catch (error) {
    return;
  }

  root.dataset.apAvailabilityInitialized = 'true';

  const preparedProducts = products.map((product) => ({
    ...product,
    skyddsklass: String(product.skyddsklass || ''),
    sizes: Array.isArray(product.sizes) ? product.sizes.map(String) : [],
    esd: apBool(product.esd),
    vattenresistent: apBool(product.vattenresistent),
    varmfodrad: apBool(product.varmfodrad)
  }));

  const classButtons = Array.from(root.querySelectorAll('[data-ap-class]'));
  const sizeButtons = Array.from(root.querySelectorAll('[data-ap-size]'));
  const featureInputs = Array.from(root.querySelectorAll('[data-ap-feature]'));
  const sizeToggles = Array.from(root.querySelectorAll('[data-ap-size-toggle]'));

  const getState = () => ({
    classes: new Set(
      classButtons
        .filter((button) => (button.dataset.apClass || '') && apIsActive(button))
        .map((button) => button.dataset.apClass)
    ),
    sizes: new Set(
      sizeButtons
        .filter(apIsActive)
        .map((button) => String(button.dataset.apSize || ''))
        .filter(Boolean)
    ),
    features: featureInputs
      .filter((input) => input instanceof HTMLInputElement && input.checked)
      .map((input) => input.dataset.apFeature)
      .filter(Boolean)
  });

  const matchesFeatures = (product, features) => features.every((feature) => product[feature] === true);

  const updateSizeToggles = () => {
    sizeToggles.forEach((toggle) => {
      const side = toggle.dataset.apSizeToggle;
      const panel = root.querySelector(`[data-ap-size-panel="${side}"]`);
      const panelButtons = panel ? Array.from(panel.querySelectorAll('[data-ap-size]')) : [];
      const available = panelButtons.some((button) => !button.disabled);
      apSetButtonAvailability(toggle, available, false);
    });
  };

  const updateAvailability = () => {
    const state = getState();

    classButtons.forEach((button) => {
      const candidateClass = button.dataset.apClass || '';
      const active = candidateClass === '' ? state.classes.size === 0 : state.classes.has(candidateClass);

      if (candidateClass === '') {
        apSetButtonAvailability(button, true, active);
        return;
      }

      const available = preparedProducts.some((product) => {
        if (product.skyddsklass !== candidateClass) return false;
        if (state.sizes.size && !product.sizes.some((size) => state.sizes.has(size))) return false;
        return matchesFeatures(product, state.features);
      });

      apSetButtonAvailability(button, available, active);
    });

    sizeButtons.forEach((button) => {
      const candidateSize = String(button.dataset.apSize || '');
      const active = state.sizes.has(candidateSize);

      const available = preparedProducts.some((product) => {
        if (state.classes.size && !state.classes.has(product.skyddsklass)) return false;
        if (!product.sizes.includes(candidateSize)) return false;
        return matchesFeatures(product, state.features);
      });

      apSetButtonAvailability(button, available, active);
    });

    featureInputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const candidate = input.dataset.apFeature;
      if (!candidate) return;

      const candidateFeatures = Array.from(new Set([...state.features, candidate]));
      const available = preparedProducts.some((product) => {
        if (state.classes.size && !state.classes.has(product.skyddsklass)) return false;
        if (state.sizes.size && !product.sizes.some((size) => state.sizes.has(size))) return false;
        return matchesFeatures(product, candidateFeatures);
      });

      apSetFeatureAvailability(input, available, '.ap-feature');
    });

    updateSizeToggles();
  };

  root.addEventListener('click', () => apScheduleUpdate(updateAvailability));
  root.addEventListener('change', () => apScheduleUpdate(updateAvailability));
  apScheduleUpdate(updateAvailability);
}

function initArbetsPROFilterAvailability() {
  initArbetsPROAllProductsAvailability();
  initArbetsPROShoeAvailability();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initArbetsPROFilterAvailability, { once: true });
} else {
  initArbetsPROFilterAvailability();
}

document.addEventListener('shopify:section:load', () => {
  requestAnimationFrame(initArbetsPROFilterAvailability);
});
